"use client";

import { useActionState } from "react";
import { useFormStatus } from "react-dom";
import {
  CheckCircleIcon,
  PaperPlaneTiltIcon,
  SpinnerGapIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { submitContactAction, type ContactFormState } from "@/lib/contact/actions";

function SubmitButton() {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      className="public-button public-button--primary contact-submit-btn"
      disabled={pending}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        width: "100%",
        minHeight: "48px",
        fontSize: "1rem",
        fontWeight: 700,
        borderRadius: "10px",
        cursor: pending ? "wait" : "pointer",
      }}
    >
      {pending ? (
        <>
          <SpinnerGapIcon size={20} className="spin" />
          <span>Gönderiliyor…</span>
        </>
      ) : (
        <>
          <PaperPlaneTiltIcon size={19} weight="bold" />
          <span>Mesajı Gönder</span>
        </>
      )}
    </button>
  );
}

export function ContactForm({ showPrices = true }: { showPrices?: boolean }) {
  const initial: ContactFormState = { status: "idle" };
  const [state, formAction] = useActionState(submitContactAction, initial);

  if (state.status === "success") {
    return (
      <div className="contact-form-success" role="status">
        <div className="contact-form-success__icon">
          <CheckCircleIcon size={48} weight="fill" color="#16a34a" />
        </div>
        <h3>Talebiniz Alındı!</h3>
        <p>{state.message}</p>
        {state.reference && (
          <div className="contact-form-success__ref">
            <span>Başvuru Takip Numarası:</span>
            <strong>{state.reference}</strong>
          </div>
        )}
        <p className="contact-form-success__note">
          Danışmanlarımız mesajınızı inceleyerek en geç 24 saat içerisinde belirttiğiniz iletişim kanallarından size geri dönüş sağlayacaktır.
        </p>
      </div>
    );
  }

  return (
    <form className="contact-form" action={formAction} noValidate>
      {state.status === "error" && (
        <div className="form-alert form-alert--error contact-form-alert" role="alert">
          <WarningCircleIcon size={20} weight="fill" />
          <div>
            <strong>{state.message}</strong>
          </div>
        </div>
      )}

      <div className="contact-form__grid">
        <label className="field">
          <span>Adınız ve Soyadınız *</span>
          <input
            name="fullName"
            type="text"
            required
            placeholder="Örn: Ahmet Yılmaz"
            autoComplete="name"
            maxLength={100}
          />
          {state.errors?.fullName && (
            <small className="field-error">{state.errors.fullName}</small>
          )}
        </label>

        <label className="field">
          <span>E-posta Adresiniz *</span>
          <input
            name="email"
            type="email"
            required
            placeholder="ahmet@example.com"
            autoComplete="email"
            maxLength={150}
          />
          {state.errors?.email && (
            <small className="field-error">{state.errors.email}</small>
          )}
        </label>
      </div>

      <div className="contact-form__grid">
        <label className="field">
          <span>Telefon Numaranız *</span>
          <input
            name="phone"
            type="tel"
            required
            placeholder="05XX XXX XX XX"
            autoComplete="tel"
            maxLength={25}
          />
          {state.errors?.phone && (
            <small className="field-error">{state.errors.phone}</small>
          )}
        </label>

        <label className="field">
          <span>Konu *</span>
          <select name="subject" defaultValue="Genel Bilgi & Danışmanlık">
            <option value="Genel Bilgi & Danışmanlık">Genel Bilgi & Danışmanlık</option>
            <option value="Rezervasyon & Sefer Soruları">Rezervasyon & Sefer Soruları</option>
            <option value="Grup & Özel Organizasyon">Grup & Özel Organizasyon</option>
            <option value="Vize & Liman İşlemleri">Vize & Liman İşlemleri</option>
            {showPrices ? (
              <option value="Fiyat Teklifi Talebi">Fiyat Teklifi Talebi</option>
            ) : null}
            <option value="Diğer">Diğer</option>
          </select>
          {state.errors?.subject && (
            <small className="field-error">{state.errors.subject}</small>
          )}
        </label>
      </div>

      <label className="field">
        <span>Mesajınız *</span>
        <textarea
          name="message"
          rows={5}
          required
          maxLength={3000}
          placeholder="İlgilendiğiniz rota, tarih aralığı, kişi sayısı veya danışmak istediğiniz detayları buraya yazabilirsiniz..."
        />
        {state.errors?.message && (
          <small className="field-error">{state.errors.message}</small>
        )}
      </label>

      {/* Honeypot field for spam prevention */}
      <div style={{ display: "none" }} aria-hidden="true">
        <label>
          Lütfen bu alanı doldurmayın
          <input
            name="verificationField"
            tabIndex={-1}
            autoComplete="off"
          />
        </label>
      </div>

      <SubmitButton />
    </form>
  );
}
